import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoCnpj,
  CampoCpf,
  CampoDeSelecao,
  CampoTelefone,
} from '../componentes/ui';
import { useConsultaDeCnpj } from '../hooks/useConsultaDeCnpj';
import { mascararTelefone, erroDoDocumento } from '../servicos/documento';
import { requisicaoPublica } from '../servicos/http';
import { ErroDaApi } from './ErroDaApi';

/**
 * Wizard público de cadastro (RN-318 a RN-330).
 *
 * ## Duas etapas, um rascunho
 *
 * Cada etapa grava no mesmo pré-cadastro (`POST /register/:id`). Não é
 * autosave por capricho: o visitante não tem conta, e a etapa 2 pede CNPJ,
 * endereço e senha — perder isso porque a aba fechou significa não voltar.
 * O id vai para a URL (`/cadastro/:id`), então o link é a retomada.
 *
 * Eram três etapas, com a senha sozinha na última. A lista de 15/09 (item 4)
 * juntou senha e documentos à etapa da empresa: um passo a menos para
 * abandonar.
 *
 * ## O CNPJ puxa o resto, sem botão
 *
 * `GET /integracoes/:cnpj` preenche razão social, endereço, telefone e e-mail
 * a partir da Receita assim que o número fecha (item 3). Quem chega da
 * calculadora já tem o que o SPED trouxe (item 2) — a consulta completa só o
 * que faltou, a cidade em geral, e não sobrescreve o que o arquivo fiscal
 * disse. Quando a consulta falha — os serviços públicos limitam por IP —, os
 * campos ficam editáveis e o cadastro segue. Consulta é conveniência; digitar
 * sempre funciona.
 *
 * ## O que este wizard não faz
 *
 * Não cria conta. Cria um pré-cadastro que entra na fila de aprovação, e é o
 * admin quem provisiona empresa, pessoa e acesso (RN-330). Um formulário
 * público que cria acesso direto é um formulário público que cria acesso para
 * quem quiser.
 *
 * ## O que vem da calculadora
 *
 * Quem chega por "Finalizar meu cadastro" já disse na calculadora qual é o
 * vínculo com a empresa e se gerencia uma carteira de empresas (assessor). O
 * wizard reidrata os dois do rascunho e só manda o perfil de volta quando a
 * pessoa mexe nele — um passo que não fala de perfil não pode desfazer o tick.
 * O vínculo não tem campo aqui: fica o que a calculadora gravou.
 *
 * Para o assessor, a etapa 2 pede **a empresa dele** (é ela que vira Parceiro
 * ICMS na aprovação, RN-325) e deixa a empresa cliente opcional — o SPED que
 * ele simulou já preencheu o cliente, e sem esses dados a aprovação recusaria
 * com "faltam CNPJ, razão social ou e-mail do assessor".
 */

type Etapa = 1 | 2;

/** A API embrulha: `{registro: {...}}`, e não o registro na raiz. */
interface RespostaRegistro {
  readonly registro: RegistroPublico;
}

interface RegistroPublico {
  readonly id: string;
  readonly personFullName: string | null;
  readonly personEmailAddress: string | null;
  readonly personTaxpayerId: string | null;
  readonly personPhone: string | null;
  readonly taxpayerId: string | null;
  readonly legalName: string | null;
  readonly emailAddress: string | null;
  readonly phone: string | null;
  readonly addressPostalCode: string | null;
  readonly addressStreetName: string | null;
  readonly addressStreetNumber: string | null;
  readonly addressDistrict: string | null;
  readonly addressCity: string | null;
  readonly addressStateCode: string | null;
  readonly personOccupation?: string | null;
  /** Já decidido pelo servidor: coluna e `customVariables` juntos. */
  readonly perfilAssessor?: boolean | null;
  /** A empresa do assessor mora aqui (`personBusiness*`), como no Deca 1. */
  readonly customVariables?: Record<string, unknown> | null;
  readonly aprovado?: boolean;
  readonly rejeitado?: boolean;
  readonly motivoRejeicao?: string | null;
}

interface Formulario {
  perfilAssessor: string;
  /** Empresa de assessoria — só quando `perfilAssessor === 'true'`. */
  assessorTaxpayerId: string;
  assessorLegalName: string;
  assessorEmailAddress: string;
  assessorPhone: string;
  personFullName: string;
  personEmailAddress: string;
  personTaxpayerId: string;
  personPhone: string;
  senha: string;
  taxpayerId: string;
  legalName: string;
  emailAddress: string;
  phone: string;
  addressPostalCode: string;
  addressStreetName: string;
  addressStreetNumber: string;
  addressDistrict: string;
  addressCity: string;
  addressStateCode: string;
}

const VAZIO: Formulario = {
  perfilAssessor: 'false',
  assessorTaxpayerId: '',
  assessorLegalName: '',
  assessorEmailAddress: '',
  assessorPhone: '',
  personFullName: '',
  personEmailAddress: '',
  personTaxpayerId: '',
  personPhone: '',
  senha: '',
  taxpayerId: '',
  legalName: '',
  emailAddress: '',
  phone: '',
  addressPostalCode: '',
  addressStreetName: '',
  addressStreetNumber: '',
  addressDistrict: '',
  addressCity: '',
  addressStateCode: '',
};

export function CadastroPublico(): JSX.Element {
  const { registroId } = useParams<{ registroId: string }>();
  const navegar = useNavigate();

  const [etapa, setEtapa] = useState<Etapa>(1);
  const [form, setForm] = useState<Formulario>(VAZIO);
  // Só o que a pessoa mudou aqui volta ao servidor; ver `corpoDaEtapa`.
  const [perfilMexido, setPerfilMexido] = useState(false);

  const definir = <C extends keyof Formulario>(campo: C, valor: Formulario[C]): void => {
    setForm((f) => ({ ...f, [campo]: valor }));
  };

  // Retomada: com id na URL, o formulário nasce do que já foi gravado.
  const existente = useQuery({
    queryKey: ['registro-publico', registroId],
    queryFn: () => requisicaoPublica<RegistroPublico>(`/register/${registroId ?? ''}`),
    enabled: registroId !== undefined,
  });

  useEffect(() => {
    const dados = existente.data;
    if (dados === undefined) return;
    const empresaAssessor = dados.customVariables ?? {};
    const texto = (chave: string): string | null => {
      const valor = empresaAssessor[chave];
      return typeof valor === 'string' && valor !== '' ? valor : null;
    };
    setForm((f) => ({
      ...f,
      perfilAssessor: dados.perfilAssessor === true ? 'true' : 'false',
      assessorTaxpayerId: texto('personBusinessTaxpayerId') ?? f.assessorTaxpayerId,
      assessorLegalName: texto('personBusinessLegalName') ?? f.assessorLegalName,
      assessorEmailAddress: texto('personBusinessEmailAddress') ?? f.assessorEmailAddress,
      assessorPhone: texto('personBusinessPhone') ?? f.assessorPhone,
      personFullName: dados.personFullName ?? f.personFullName,
      personEmailAddress: dados.personEmailAddress ?? f.personEmailAddress,
      personTaxpayerId: dados.personTaxpayerId ?? f.personTaxpayerId,
      personPhone: dados.personPhone ?? f.personPhone,
      taxpayerId: dados.taxpayerId ?? f.taxpayerId,
      legalName: dados.legalName ?? f.legalName,
      emailAddress: dados.emailAddress ?? f.emailAddress,
      phone: dados.phone ?? f.phone,
      addressPostalCode: dados.addressPostalCode ?? f.addressPostalCode,
      addressStreetName: dados.addressStreetName ?? f.addressStreetName,
      addressStreetNumber: dados.addressStreetNumber ?? f.addressStreetNumber,
      addressDistrict: dados.addressDistrict ?? f.addressDistrict,
      addressCity: dados.addressCity ?? f.addressCity,
      addressStateCode: dados.addressStateCode ?? f.addressStateCode,
    }));
  }, [existente.data]);

  const gravar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      registroId === undefined
        ? requisicaoPublica<RespostaRegistro>('/register', { metodo: 'POST', corpo })
        : requisicaoPublica<RespostaRegistro>(`/register/${registroId}`, {
            metodo: 'POST',
            corpo,
          }),
  });

  // Item 5 de 15/09 — o documento errado não sai da tela. As mensagens já
  // aparecem embaixo do campo; isto só impede o envio enquanto houver alguma.
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const problemas = problemasDaEtapa(form, etapa);

  const avancar = (proxima: Etapa | 'fim'): void => {
    setTentouEnviar(true);
    if (problemas.length > 0) return;
    setTentouEnviar(false);

    const corpo = corpoDaEtapa(form, etapa, proxima, perfilMexido);

    gravar.mutate(corpo, {
      onSuccess: (resposta) => {
        if (proxima === 'fim') {
          navegar('/cadastro/sucesso');
          return;
        }
        // O id só existe depois da primeira gravação; a partir daí ele vive na
        // URL, e recarregar a página não perde o que foi digitado.
        if (registroId === undefined) {
          navegar(`/cadastro/${resposta.registro.id}`, { replace: true });
        }
        setEtapa(proxima);
      },
    });
  };

  const jaAprovado = existente.data?.aprovado === true;
  const rejeitado = existente.data?.rejeitado === true;

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-10 sm:px-8">
      <header className="flex flex-col gap-2">
        <Link
          to="/"
          className="text-sm text-conteudo-secundario underline-offset-4 hover:underline"
        >
          ← Voltar ao site
        </Link>
        <h1 className="font-display text-3xl font-bold tracking-tight">Cadastro</h1>
        <p className="max-w-2xl text-sm text-conteudo-secundario">
          Preencha os dados para entrar na fila de análise. Um contato da MS3 Capital confere as
          informações e libera o acesso.
        </p>
      </header>

      <Passos etapa={etapa} />

      {rejeitado && (
        <Aviso tom="atencao" titulo="Este cadastro foi recusado">
          {existente.data?.motivoRejeicao ??
            'Entre em contato com a MS3 Capital para entender o motivo.'}
        </Aviso>
      )}

      {jaAprovado && (
        <Aviso tom="positivo" titulo="Este cadastro já foi aprovado">
          Não é preciso preencher de novo. Use <Link to="/entrar">a tela de acesso</Link> com o
          e-mail informado.
        </Aviso>
      )}

      <form
        className="flex flex-col gap-5 rounded-card border border-borda bg-superficie-elevada p-6"
        onSubmit={(e) => {
          e.preventDefault();
          avancar(etapa === 2 ? 'fim' : 2);
        }}
      >
        {etapa === 1 && (
          <>
            <CampoDeSelecao
              rotulo="Você está se cadastrando como"
              value={form.perfilAssessor}
              onChange={(e) => {
                definir('perfilAssessor', e.target.value);
                setPerfilMexido(true);
              }}
              ajuda={
                existente.data?.perfilAssessor === true
                  ? 'Você marcou na calculadora que gerencia uma carteira de empresas. Assessor intermedia crédito para outras empresas; contribuinte compra crédito para a própria.'
                  : 'Assessor intermedia crédito para outras empresas; contribuinte compra crédito para a própria.'
              }
              opcoes={[
                { valor: 'false', rotulo: 'Contribuinte (compro crédito de ICMS)' },
                { valor: 'true', rotulo: 'Assessor / parceiro' },
              ]}
            />
            <Campo
              rotulo="Seu nome completo"
              required
              value={form.personFullName}
              onChange={(e) => definir('personFullName', e.target.value)}
            />
            <Campo
              rotulo="Seu e-mail"
              type="email"
              required
              ajuda="É por ele que o acesso é liberado. Retomar o cadastro com o mesmo e-mail continua de onde parou."
              value={form.personEmailAddress}
              onChange={(e) => definir('personEmailAddress', e.target.value)}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoCpf
                rotulo="Seu CPF"
                required
                ajuda="Obrigatório: a aprovação cria a pessoa no cadastro, e ela é identificada pelo CPF."
                value={form.personTaxpayerId}
                onChange={(valor) => definir('personTaxpayerId', valor)}
              />
              <CampoTelefone
                rotulo="Telefone"
                value={form.personPhone}
                onChange={(valor) => definir('personPhone', valor)}
              />
            </div>
          </>
        )}

        {etapa === 2 && (
          <>
            {form.perfilAssessor === 'true' && <EtapaDaAssessoria form={form} definir={definir} />}
            <EtapaDaEmpresa
              form={form}
              definir={definir}
              assessor={form.perfilAssessor === 'true'}
              // Enquanto o rascunho não chegou, consultar seria preencher por
              // cima do que a calculadora gravou — e depois o rascunho por
              // cima da consulta.
              reidratado={registroId === undefined || existente.data !== undefined}
            />

            {registroId !== undefined && <Documentos registroId={registroId} />}

            <Campo
              rotulo="Senha de acesso"
              type="password"
              required
              autoComplete="new-password"
              ajuda="Ela só passa a valer quando o cadastro for aprovado."
              value={form.senha}
              onChange={(e) => definir('senha', e.target.value)}
            />
            <Aviso tom="informacao" titulo="O que acontece depois">
              O cadastro entra na fila de análise. Assim que for aprovado, você recebe um e-mail com
              o endereço de acesso — a senha é a que acabou de definir.
            </Aviso>
          </>
        )}

        {tentouEnviar && problemas.length > 0 && (
          <Aviso tom="atencao" titulo="Confira os campos marcados">
            {problemas.join(' ')}
          </Aviso>
        )}
        {gravar.isError && <ErroDaApi titulo="Não foi possível gravar" erro={gravar.error} />}

        <div className="flex flex-wrap justify-between gap-3">
          <Botao
            type="button"
            variante="secundario"
            disabled={etapa === 1}
            onClick={() => setEtapa(1)}
          >
            Voltar
          </Botao>
          {/*
            Enquanto o rascunho carrega, não grava: um envio antes da
            reidratação mandaria o formulário vazio por cima do que a
            calculadora já registrou.
          */}
          <Botao type="submit" carregando={gravar.isPending || existente.isLoading}>
            {etapa === 2 ? 'Enviar cadastro' : 'Continuar'}
          </Botao>
        </div>
      </form>
    </main>
  );
}

/* ─── Etapa da empresa, com consulta de CNPJ ──────────────────────────────── */

function EtapaDaEmpresa(props: {
  readonly form: Formulario;
  readonly definir: <C extends keyof Formulario>(campo: C, valor: Formulario[C]) => void;
  /** Para o assessor, o cliente é opcional: a empresa obrigatória é a dele. */
  readonly assessor: boolean;
  /** O rascunho já foi lido — só então a consulta pode completar os campos. */
  readonly reidratado: boolean;
}): JSX.Element {
  const { form, definir, assessor, reidratado } = props;

  /**
   * A Receita completa, não substitui: o que veio do SPED (razão social,
   * endereço do registro 0005) ou o que a pessoa já digitou fica. Só campo em
   * branco recebe o valor consultado. A cidade é o caso típico — o SPED não a
   * traz, a Receita sim.
   */
  const consulta = useConsultaDeCnpj(form.taxpayerId, {
    publica: true,
    ativa: reidratado,
    aoCarregar: (dados) => {
      const completar = <C extends keyof Formulario>(campo: C, valor: string | null): void => {
        if (valor !== null && valor !== '' && form[campo] === '') definir(campo, valor);
      };
      completar('legalName', dados.razaoSocial);
      completar('addressStreetName', dados.endereco.logradouro);
      completar('addressStreetNumber', dados.endereco.numero);
      completar('addressDistrict', dados.endereco.bairro);
      completar('addressCity', dados.endereco.cidade);
      completar('addressStateCode', dados.endereco.uf);
      completar('addressPostalCode', dados.endereco.cep);
      // Mascarado: a Receita devolve dois números na mesma string, e o que
      // o campo mostra tem de ser o que o formulário manda.
      completar('phone', mascararTelefone(dados.telefone ?? ''));
      completar('emailAddress', dados.email);
    },
  });

  return (
    <>
      {assessor && (
        <div>
          <h2 className="text-sm font-semibold text-conteudo-primario">
            Empresa cliente que será calculada{' '}
            <span className="font-normal text-conteudo-sutil">(opcional)</span>
          </h2>
          <p className="mt-1 text-xs text-conteudo-secundario">
            Se você simulou com o SPED de um cliente, os dados dele já estão aqui. Pode deixar em
            branco e cadastrar os clientes depois, pela plataforma.
          </p>
        </div>
      )}
      <CampoCnpj
        rotulo="CNPJ da empresa"
        required={!assessor}
        className="w-full sm:w-72"
        ajuda={
          consulta.pendente
            ? 'Consultando a Receita…'
            : 'Os dados públicos da empresa são preenchidos automaticamente.'
        }
        value={form.taxpayerId}
        onChange={(valor) => definir('taxpayerId', valor)}
      />

      <AvisosDaConsulta consulta={consulta} />

      <Campo
        rotulo="Razão social"
        required={!assessor}
        value={form.legalName}
        onChange={(e) => definir('legalName', e.target.value)}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="E-mail da empresa"
          type="email"
          value={form.emailAddress}
          onChange={(e) => definir('emailAddress', e.target.value)}
        />
        <CampoTelefone
          rotulo="Telefone da empresa"
          value={form.phone}
          onChange={(valor) => definir('phone', valor)}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
        <Campo
          rotulo="Logradouro"
          value={form.addressStreetName}
          onChange={(e) => definir('addressStreetName', e.target.value)}
        />
        <Campo
          rotulo="Número"
          value={form.addressStreetNumber}
          onChange={(e) => definir('addressStreetNumber', e.target.value)}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="Bairro"
          value={form.addressDistrict}
          onChange={(e) => definir('addressDistrict', e.target.value)}
        />
        <Campo
          rotulo="CEP"
          inputMode="numeric"
          value={form.addressPostalCode}
          onChange={(e) => definir('addressPostalCode', e.target.value)}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
        <Campo
          rotulo="Cidade"
          value={form.addressCity}
          onChange={(e) => definir('addressCity', e.target.value)}
        />
        <Campo
          rotulo="UF"
          maxLength={2}
          value={form.addressStateCode}
          onChange={(e) => definir('addressStateCode', e.target.value.toUpperCase())}
        />
      </div>
    </>
  );
}

/** O que a consulta tem a dizer — cadastrada, falhou, carregou. */
function AvisosDaConsulta(props: {
  readonly consulta: ReturnType<typeof useConsultaDeCnpj>;
}): JSX.Element | null {
  const { consulta } = props;

  if (consulta.checagem?.jaCadastrado === true) {
    return (
      <Aviso tom="informacao" titulo="Esta empresa já está no sistema">
        {consulta.checagem.razaoSocialCadastrada ?? 'A empresa'} já tem cadastro. Você pode seguir —
        a análise vai vincular seu acesso a ela em vez de criar outra.
      </Aviso>
    );
  }
  if (consulta.erro !== null) {
    return (
      <Aviso tom="atencao" titulo="Não consegui consultar a Receita">
        Os serviços públicos de consulta limitam o número de buscas. Preencha os campos à mão — o
        cadastro segue normalmente.
        <div className="mt-3">
          <Botao type="button" variante="secundario" tamanho="pequeno" onClick={consulta.repetir}>
            Tentar de novo
          </Botao>
        </div>
      </Aviso>
    );
  }
  if (consulta.dados !== null) {
    return (
      <Aviso tom="positivo" titulo="Dados da Receita Federal carregados">
        Situação: {consulta.dados.situacao ?? 'não informada'}. Confira e ajuste o que estiver
        desatualizado.
      </Aviso>
    );
  }
  return null;
}

/* ─── Etapa da empresa do assessor ────────────────────────────────────────── */

/**
 * RN-325 — a empresa **do assessor**, que não tem colunas próprias no
 * pré-cadastro: as colunas de empresa descrevem o Cliente da simulação. Vai em
 * `customVariables.personBusiness*`, que é de onde a aprovação a lê para criar
 * o Parceiro ICMS. É o bloco "Dados da sua Empresa de Assessoria" do Deca 1.
 */
function EtapaDaAssessoria(props: {
  readonly form: Formulario;
  readonly definir: <C extends keyof Formulario>(campo: C, valor: Formulario[C]) => void;
}): JSX.Element {
  const { form, definir } = props;

  const consulta = useConsultaDeCnpj(form.assessorTaxpayerId, {
    publica: true,
    aoCarregar: (dados) => {
      if (form.assessorLegalName === '') definir('assessorLegalName', dados.razaoSocial);
      if (form.assessorEmailAddress === '' && dados.email !== null)
        definir('assessorEmailAddress', dados.email);
      if (form.assessorPhone === '' && dados.telefone !== null)
        definir('assessorPhone', mascararTelefone(dados.telefone));
    },
  });

  return (
    <section className="flex flex-col gap-4 rounded-card border border-borda p-4">
      <div>
        <h2 className="text-sm font-semibold text-conteudo-primario">Sua empresa de assessoria</h2>
        <p className="mt-1 text-xs text-conteudo-secundario">
          É ela que passa a intermediar crédito na plataforma. A aprovação cria o cadastro dela e
          vincula você como gestor.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <CampoCnpj
          rotulo="CNPJ da assessoria"
          required
          ajuda={consulta.pendente ? 'Consultando a Receita…' : undefined}
          value={form.assessorTaxpayerId}
          onChange={(valor) => definir('assessorTaxpayerId', valor)}
        />
        <Campo
          rotulo="Razão social da assessoria"
          required
          value={form.assessorLegalName}
          onChange={(e) => definir('assessorLegalName', e.target.value)}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="E-mail da assessoria"
          type="email"
          required
          value={form.assessorEmailAddress}
          onChange={(e) => definir('assessorEmailAddress', e.target.value)}
        />
        <CampoTelefone
          rotulo="Telefone da assessoria"
          value={form.assessorPhone}
          onChange={(valor) => definir('assessorPhone', valor)}
        />
      </div>
    </section>
  );
}

/* ─── Documentos ──────────────────────────────────────────────────────────── */

interface DocumentoDoRegistro {
  readonly tipo: string;
  readonly nome: string;
  readonly tamanho: number;
  readonly enviadoEm: string;
}

const TIPOS_DE_DOCUMENTO = [
  { valor: 'CONTRATO_SOCIAL', rotulo: 'Contrato social' },
  { valor: 'CARTAO_CNPJ', rotulo: 'Cartão CNPJ' },
  { valor: 'DOCUMENTO_REPRESENTANTE', rotulo: 'Documento do representante' },
  { valor: 'PROCURACAO', rotulo: 'Procuração' },
  { valor: 'OUTROS', rotulo: 'Outro documento' },
] as const;

/**
 * Anexos do cadastro (RN-332).
 *
 * ## Opcional, e a tela diz isso
 *
 * O cadastro é analisado por gente, e a análise pede os documentos quando
 * faltam. Travar o envio aqui só faria o visitante desistir na etapa 3 — o
 * custo de um cadastro sem contrato social é um e-mail; o de um cadastro
 * abandonado é o cliente inteiro.
 *
 * ## Base64, não multipart
 *
 * O arquivo é lido no navegador e vai no corpo JSON, do mesmo jeito que o SPED
 * e os anexos das ações do cronograma. Um caminho de upload para o sistema
 * inteiro, e não um por formulário.
 */
function Documentos(props: { readonly registroId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState<string>(TIPOS_DE_DOCUMENTO[0].valor);

  const lista = useQuery({
    queryKey: ['documentos-registro', props.registroId],
    queryFn: () =>
      requisicaoPublica<DocumentoDoRegistro[]>(`/register/${props.registroId}/documentos`),
  });

  const anexar = useMutation({
    mutationFn: async (arquivo: File) => {
      const conteudoBase64 = await lerComoBase64(arquivo);
      return requisicaoPublica<DocumentoDoRegistro[]>(`/register/${props.registroId}/documentos`, {
        metodo: 'POST',
        corpo: {
          tipo,
          nome: arquivo.name,
          conteudoBase64,
          ...(arquivo.type === '' ? {} : { tipoConteudo: arquivo.type }),
        },
      });
    },
    onSuccess: (documentos) => {
      queryClient.setQueryData(['documentos-registro', props.registroId], documentos);
    },
  });

  const documentos = lista.data ?? [];

  return (
    <section className="flex flex-col gap-3 rounded-card border border-borda p-4">
      <div>
        <h2 className="text-sm font-semibold text-conteudo-primario">
          Documentos <span className="font-normal text-conteudo-sutil">(opcional)</span>
        </h2>
        <p className="mt-1 text-xs text-conteudo-secundario">
          Contrato social, cartão CNPJ, documento do representante. Adiantam a análise, mas podem
          ser enviados depois — PDF ou imagem, até 10 MB.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <CampoDeSelecao
          rotulo="Tipo"
          className="w-56"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          opcoes={TIPOS_DE_DOCUMENTO.map((t) => ({ valor: t.valor, rotulo: t.rotulo }))}
        />
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-primario">Arquivo</span>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            disabled={anexar.isPending}
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              if (arquivo !== undefined) anexar.mutate(arquivo);
              // Zera o input: sem isso, reenviar o mesmo arquivo depois de um
              // erro não dispara `change` e parece que o botão não funciona.
              e.target.value = '';
            }}
            className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm file:text-conteudo-primario"
          />
        </label>
      </div>

      {anexar.isPending && <p className="text-xs text-conteudo-sutil">Enviando…</p>}
      {anexar.isError && <ErroDaApi titulo="Não foi possível anexar" erro={anexar.error} />}

      {documentos.length > 0 && (
        <ul className="flex flex-col gap-1">
          {documentos.map((d) => (
            <li
              key={`${d.tipo}-${d.nome}`}
              className="flex flex-wrap items-baseline gap-2 text-sm text-conteudo-primario"
            >
              <span aria-hidden>✓</span>
              <span>{d.nome}</span>
              <span className="text-xs text-conteudo-sutil">
                {TIPOS_DE_DOCUMENTO.find((t) => t.valor === d.tipo)?.rotulo ?? d.tipo}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** `FileReader` devolve `data:...;base64,XXX` — só o depois da vírgula interessa. */
async function lerComoBase64(arquivo: File): Promise<string> {
  const leitor = new FileReader();
  return new Promise((resolver, rejeitar) => {
    leitor.onload = () => {
      const resultado = String(leitor.result);
      resolver(resultado.slice(resultado.indexOf(',') + 1));
    };
    leitor.onerror = () => rejeitar(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

/* ─── Tela de sucesso ─────────────────────────────────────────────────────── */

export function CadastroConcluido(): JSX.Element {
  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-5 px-4 py-16 text-center sm:px-8">
      <h1 className="font-display text-3xl font-bold tracking-tight">Cadastro enviado</h1>
      <p className="text-sm text-conteudo-secundario">
        Recebemos seus dados. A análise costuma levar até um dia útil; assim que ela terminar você
        recebe um e-mail com o acesso.
      </p>
      <div className="flex justify-center gap-3">
        <Link to="/">
          <Botao variante="secundario">Voltar ao site</Botao>
        </Link>
        <Link to="/calculadora">
          <Botao>Simular meu crédito</Botao>
        </Link>
      </div>
    </main>
  );
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

function Passos(props: { readonly etapa: Etapa }): JSX.Element {
  const nomes = ['Seus dados', 'Empresa e acesso'] as const;
  return (
    <ol className="flex flex-wrap gap-2" aria-label="Etapas do cadastro">
      {nomes.map((nome, i) => {
        const numero = i + 1;
        const atual = numero === props.etapa;
        const feita = numero < props.etapa;
        return (
          <li
            key={nome}
            aria-current={atual ? 'step' : undefined}
            className={`flex items-center gap-2 rounded-card border px-3 py-2 text-sm ${
              atual
                ? 'border-acento-ciano text-acento-ciano'
                : feita
                  ? 'border-borda text-conteudo-secundario'
                  : 'border-borda text-conteudo-sutil'
            }`}
          >
            <span className="valor-fiscal text-xs">{feita ? '✓' : numero}</span>
            {nome}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * O corpo de cada etapa.
 *
 * Manda só o que a etapa coletou. Enviar o formulário inteiro a cada passo
 * sobrescreveria com string vazia o que a etapa seguinte ainda não preencheu —
 * e o servidor não tem como distinguir "apagou" de "ainda não digitou".
 *
 * O perfil segue a mesma regra: só vai quando a pessoa mexeu no select. Antes
 * ia `'false'` em todo passo, e como o servidor lia presença como "assessor",
 * todo contribuinte caía na fila do admin; hoje o servidor entende `false`,
 * mas mandar sem ter mexido apagaria o tick que a calculadora gravou.
 */
function corpoDaEtapa(
  form: Formulario,
  etapa: Etapa,
  proxima: Etapa | 'fim',
  perfilMexido: boolean,
): Record<string, unknown> {
  const assessor = form.perfilAssessor === 'true';
  const base = {
    kind: proxima === 'fim' ? 'finished' : 'draft',
    step: proxima === 'fim' ? 3 : proxima,
    ...(perfilMexido ? { perfilAssessor: assessor } : {}),
  };

  if (etapa === 1) {
    return {
      ...base,
      personFullName: form.personFullName,
      personEmailAddress: form.personEmailAddress,
      personTaxpayerId: form.personTaxpayerId,
      ...(form.personPhone === '' ? {} : { personPhone: form.personPhone }),
    };
  }

  if (etapa === 2) {
    return {
      ...base,
      ...(assessor
        ? {
            customVariables: {
              personBusinessTaxpayerId: form.assessorTaxpayerId.replace(/\D/g, ''),
              personBusinessLegalName: form.assessorLegalName,
              personBusinessEmailAddress: form.assessorEmailAddress,
              ...(form.assessorPhone === '' ? {} : { personBusinessPhone: form.assessorPhone }),
            },
          }
        : {}),
      ...(form.taxpayerId === '' ? {} : { taxpayerId: form.taxpayerId }),
      ...(form.legalName === '' ? {} : { legalName: form.legalName }),
      ...(form.emailAddress === '' ? {} : { emailAddress: form.emailAddress }),
      ...(form.phone === '' ? {} : { phone: form.phone }),
      ...(form.addressPostalCode === '' ? {} : { addressPostalCode: form.addressPostalCode }),
      ...(form.addressStreetName === '' ? {} : { addressStreetName: form.addressStreetName }),
      ...(form.addressStreetNumber === '' ? {} : { addressStreetNumber: form.addressStreetNumber }),
      ...(form.addressDistrict === '' ? {} : { addressDistrict: form.addressDistrict }),
      ...(form.addressCity === '' ? {} : { addressCity: form.addressCity }),
      ...(form.addressStateCode === '' ? {} : { addressStateCode: form.addressStateCode }),
      senha: form.senha,
    };
  }

  return base;
}

/**
 * Item 5 de 15/09 — CPF e CNPJ não saem da tela inválidos.
 *
 * Os campos já mostram a mensagem embaixo; isto é a lista que trava o envio.
 * Vazio não entra aqui: obrigatoriedade é do `required` de cada campo.
 */
function problemasDaEtapa(form: Formulario, etapa: Etapa): readonly string[] {
  const problemas: string[] = [];
  if (etapa === 1) {
    const cpf = erroDoDocumento(form.personTaxpayerId, 'cpf', true);
    if (cpf !== undefined) problemas.push(cpf);
    return problemas;
  }

  const cnpj = erroDoDocumento(form.taxpayerId, 'cnpj', true);
  if (cnpj !== undefined) problemas.push(`Empresa: ${cnpj}`);
  if (form.perfilAssessor === 'true') {
    const assessoria = erroDoDocumento(form.assessorTaxpayerId, 'cnpj', true);
    if (assessoria !== undefined) problemas.push(`Assessoria: ${assessoria}`);
  }
  return problemas;
}
